'use client';

import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import type { CampaignConfig } from '../config.interface';
import { useResponsiveBreakpoints } from '@/hooks/useResponsiveBreakpoints';
import { useComputedColor } from '@/hooks/useComputedColor';
import { LinearProgressBar } from '@/ui/LinearProgressBar';
import { CircularProgressBar } from '@/ui/CircularProgressBar';
import { useCorporateStore } from '@/infrastructure/zustand/corporateStoreProvider';
import { useMoneyStore } from '@/infrastructure/zustand/moneyStoreProvider';
import { useLanguageStore } from '@/infrastructure/zustand/languageStoreProvider';
import { formatNumber } from '@/infrastructure/zustand/utils';
import { transformToLocale } from '@/infrastructure/zustand/languageStore';

export const CampaignCollectedCounter = ({
	collected,
	donors,
	percentageCollected,
	config,
}: {
	collected: number;
	donors: number;
	percentageCollected: number;
	config: CampaignConfig;
}) => {
	const { i18n } = useTranslation();
	const { extraSmall, small, large, medium, extraLarge } = useResponsiveBreakpoints();
	const lang = useLanguageStore((state) => transformToLocale(state.currentLangCode));
	const collectedWithCurrency = useMoneyStore((state) => state.formatAmountWithCurrency(collected, lang));
	const targetWithCurrency = useMoneyStore((state) =>
		state.formatAmountWithCurrency(config.campaign.budget.funding ?? 0, lang),
	);
	const computedColor = useComputedColor();
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

	const width = extraSmall || small || medium || large ? '70%' : '60%';
	const displayMode = small || medium || large ? 'flex' : 'block';
	const justifyContent = extraSmall || small || medium || large ? 'space-around' : '';

	const sxvalueText = {
		fontSize: '30px',
		fontWeight: 'bold',
		lineHeight: '30px',
		textAlign: 'left',
	};

	const sxvalueSubtitle = {
		'fontSize': '14px',
		'lineHeight': '14px',
		'marginTop': '3px',
		'textAlign': 'left',
		'& > p': {
			margin: 0,
		},
		'marginBottom': '20px',
		[theme.breakpoints.up('sm')]: {
			marginBottom: '30px',
		},
	};

	return (
		<>
			<Box
				width={width}
				display={displayMode}
				justifyContent={justifyContent}
				paddingTop={isDesktop ? '2rem' : undefined}
			>
				<Box>
					<Typography sx={sxvalueText} style={{ color: computedColor }}>
						{formatNumber(i18n.language, donors)}
					</Typography>
					<Typography sx={sxvalueSubtitle} style={{ color: computedColor }}>
						<Trans i18nKey="donations" />
					</Typography>
				</Box>
				<Box hidden={config.hideCollectedCounter}>
					<Typography sx={sxvalueText} style={{ color: computedColor }}>
						{collectedWithCurrency}
					</Typography>
					<Typography sx={sxvalueSubtitle} style={{ color: computedColor }}>
						{!config.hideTotalDonationsAmountObjective ? (
							<>
								<Trans i18nKey="donatedOf" /> {targetWithCurrency}
							</>
						) : (
							<Trans i18nKey="donated" />
						)}
					</Typography>
				</Box>
			</Box>
			{!config.hidePercentFunded && (
				<PercentFundedCircle
					small={small}
					medium={medium}
					large={large}
					extraSmall={extraSmall}
					extraLarge={extraLarge}
					percentageCollected={percentageCollected}
					computedColor={computedColor}
				/>
			)}
		</>
	);
};

type PercentProps = {
	small: boolean;
	medium: boolean;
	large: boolean;
	extraSmall: boolean;
	extraLarge: boolean;
	percentageCollected: number;
	computedColor: string;
};

const PercentFundedCircle = ({
	small,
	medium,
	large,
	percentageCollected,
	computedColor,
	extraSmall,
	extraLarge,
}: PercentProps) => {
	const [trans] = useTranslation();
	const themeBaseColor = useCorporateStore((state) => state.metadata.themeBaseColor);

	if (small || medium || large) {
		return (
			<Box display="flex" flexDirection="column" style={{ textAlign: 'left' }}>
				<Box>
					<Typography
						style={{
							fontSize: '30px',
							lineHeight: '30px',
							fontWeight: 900,
							color: computedColor,
						}}
					>
						{Math.round(percentageCollected)}%
						<Typography style={{ fontSize: '13px', fontWeight: 400, color: computedColor }} component="span">
							{' '}
							{trans('funded')}
						</Typography>
					</Typography>
				</Box>
				<Box>
					<LinearProgressBar value={percentageCollected} backgroundColor={themeBaseColor.trim()} />
				</Box>
			</Box>
		);
	}

	return (
		<Box
			width={`${extraSmall || extraLarge ? '120px' : '120px'}`}
			maxWidth={`${extraSmall || extraLarge ? '100%' : '120px'}`}
		>
			<CircularProgressBar value={percentageCollected} backgroundColor={themeBaseColor.trim()} />
		</Box>
	);
};
